import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { api, type Project } from "./api";

type ProjectState = {
  projects: Project[];
  selected: Project | null;
  loading: boolean;
  setSelectedId: (id: string) => void;
  reload: () => Promise<void>;
};

const Ctx = createContext<ProjectState | null>(null);
const LS_KEY = "insight.project";

export function ProjectProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(() => localStorage.getItem(LS_KEY));
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    const list = await api.projects();
    setProjects(list);
    setSelectedId((cur) => (cur && list.some((p) => p.id === cur) ? cur : (list[0]?.id ?? null)));
  }, []);

  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, [reload]);

  useEffect(() => {
    if (selectedId) localStorage.setItem(LS_KEY, selectedId);
  }, [selectedId]);

  const selected = projects.find((p) => p.id === selectedId) ?? null;

  return (
    <Ctx.Provider value={{ projects, selected, loading, setSelectedId, reload }}>
      {children}
    </Ctx.Provider>
  );
}

export function useProjects(): ProjectState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useProjects utenfor ProjectProvider");
  return ctx;
}
